import { Text, ButtonProps } from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { StaticListDetails } from "../../../common/types/campaign";
import {
  createUserMapping,
  findUserFromMapping,
  getUserStatusFromMapping,
  isLoading,
  isSuccess,
} from "../../../common/helper/commonHelper";
import { LOADING_STATES } from "../../../common/constants/common";
import { useNavigate } from "react-router-dom";
import urls from "../../../urls";
import { DeleteConfirmationModal } from "../../../components/DeleteConfirmationModal";
import IButton, { BUTTON } from "../../../components/IButton";
import EditNameModal, {
  ASSET_NAME_ACTIONS,
} from "../../../components/EditNameModal";
import {
  createStaticList,
  deleteStaticList,
  listStaticLists,
  renameStaticList,
  resetData,
  resetStaticListsList,
  selectStaticList,
  setStaticListsPage,
} from "./staticlistSlice";
import { staticListColumnDefs } from "./components/tableDefinitions";
import { debounce } from "lodash";
import { getUserList, selectAccount } from "../../account/accountSlice";
import { UserListMap } from "../../../common/types/auth";
import { InitialStateMessage } from "../../../components/InitialStateMessage";
import SpinnerContainer from "../../../components/SpinnerContainer";
import { CommonListHeader } from "../../../components/CommonListHeader";
import { DataTable } from "../../../components/data-table/DataTable";
import LayoutWithTable from "../../../layout/LayoutWithTable";

function AddStaticListButton({ ...props }: ButtonProps) {
  return (
    <IButton variant={BUTTON.PRIMARY} {...props} name="add-static-list">
      New static list
    </IButton>
  );
}

function StaticListHeader({
  clickHandler,
  loading,
  searchKeyword,
  onSearch,
}: {
  clickHandler: () => void;
  loading: LOADING_STATES;
  searchKeyword: string;
  onSearch: (value: string) => void;
}) {
  return (
    <CommonListHeader
      heading="Static lists"
      searchInputProps={{
        placeholder: "Search static lists",
        name: "search-input",
        onSearch: onSearch,
        value: searchKeyword,
      }}
      createButtonProps={{
        name: "add-static-list",
        onClick: clickHandler,
        isLoading: isLoading(loading),
        text: "New static list",
      }}
    />
  );
}

export default function StaticLists() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isEditModalOpen, setIsEditModalOpen] = useState<string | null>(null);
  const [selectedRow, setSelectedRow] = useState<StaticListDetails | null>(
    null
  );
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [usersListMap, setUsersListMap] = useState<UserListMap>({});

  const { staticLists, updatingStaticList, createdStaticListId } =
    useSelector(selectStaticList);
  const { usersList, fetchingUsersList } = useSelector(selectAccount);

  useEffect(() => {
    dispatch(listStaticLists(searchKeyword));

    return () => {
      dispatch(resetData());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, staticLists.currentPageNo]);

  useEffect(() => {
    return () => {
      dispatch(resetStaticListsList());
    };
  }, [dispatch]);

  const searchIfValid = useCallback(
    (searchText: string) => {
      if (staticLists.currentPageNo !== 1) {
        dispatch(setStaticListsPage(1));
      } else {
        dispatch(listStaticLists(searchText));
      }
    },
    [dispatch, staticLists.currentPageNo]
  );

  const debouncedSearch = useMemo(
    () => debounce(searchIfValid, 1000),
    [searchIfValid]
  );

  useEffect(() => {
    if (isSuccess(updatingStaticList)) {
      dispatch(listStaticLists(searchKeyword));
      dispatch(resetData());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, updatingStaticList]);

  useEffect(() => {
    if (createdStaticListId) {
      dispatch(resetData());
      navigate(`${urls.staticList}/${createdStaticListId}`);
    }
  }, [navigate, dispatch, createdStaticListId]);

  useEffect(() => {
    if (!usersList) {
      dispatch(getUserList());
    } else {
      let keyedUsers: UserListMap = createUserMapping(usersList);
      setUsersListMap(keyedUsers);
    }
  }, [usersList, dispatch]);

  function onDeleteClick(row: StaticListDetails) {
    setIsOpenDeleteModal(row.static_list_id);
  }

  function deleteStaticListHandler() {
    if (isOpenDeleteModal) {
      dispatch(deleteStaticList(isOpenDeleteModal));
      setIsOpenDeleteModal("");
    }
  }

  function onRenameClick(row: StaticListDetails) {
    setSelectedRow(row);
    setIsEditModalOpen(row.name);
  }

  function renameStaticListHandler(name: string) {
    if (selectedRow) {
      dispatch(
        renameStaticList({ name, staticListId: selectedRow.static_list_id })
      );
      setIsEditModalOpen(null);
      setSelectedRow(null);
    }
  }

  function createStaticListHandler(name: string) {
    setIsEditModalOpen(null);
    dispatch(createStaticList(name));
  }

  function goToStaticListDetails(list: StaticListDetails) {
    navigate(`${urls.staticList}/${list.static_list_id}`);
  }

  const getColumns = useCallback(
    () =>
      staticListColumnDefs(
        usersListMap,
        onDeleteClick,
        onRenameClick,
        findUserFromMapping,
        getUserStatusFromMapping
      ),
    [usersListMap]
  );

  return (
    <>
      <StaticListHeader
        clickHandler={() => setIsEditModalOpen("")}
        loading={updatingStaticList}
        searchKeyword={searchKeyword}
        onSearch={(value) => {
          setSearchKeyword(value);
          debouncedSearch(value);
        }}
      />
      <SpinnerContainer
        height="400px"
        width="100%"
        loading={staticLists.fetchingList || fetchingUsersList}
      >
        {staticLists.totalPageCount || searchKeyword ? (
          <LayoutWithTable>
            <DataTable
              loading={staticLists.fetchingList || fetchingUsersList}
              list={staticLists.list}
              totalPageCount={staticLists.totalPageCount}
              currentPage={staticLists.currentPageNo}
              onRowClick={goToStaticListDetails}
              setPage={(pageNo) => dispatch(setStaticListsPage(pageNo))}
              columns={getColumns()}
              emptyMsg={`No static list found.${
                searchKeyword ? " Please change the search / filter values" : ""
              }`}
              links={{ to: urls.staticList, editParam: "static_list_id" }}
            />
          </LayoutWithTable>
        ) : (
          <InitialStateMessage
            text="No static lists"
            message="Create a new static list to start adding contacts to it"
          >
            <AddStaticListButton
              onClick={() => setIsEditModalOpen("")}
              isLoading={isLoading(updatingStaticList)}
            />
          </InitialStateMessage>
        )}
      </SpinnerContainer>
      <EditNameModal
        value={isEditModalOpen ?? ""}
        action={
          isEditModalOpen
            ? ASSET_NAME_ACTIONS.RENAME
            : ASSET_NAME_ACTIONS.CREATE
        }
        isOpen={isEditModalOpen !== null}
        onClose={() => setIsEditModalOpen(null)}
        onSubmit={
          isEditModalOpen ? renameStaticListHandler : createStaticListHandler
        }
        asset="static list"
      />
      <DeleteConfirmationModal
        isOpen={!!isOpenDeleteModal}
        onCancel={() => setIsOpenDeleteModal("")}
        submitHandler={deleteStaticListHandler}
      >
        <Text>
          Are you sure about deleting this static list? Once deleted cannot be
          undone
        </Text>
      </DeleteConfirmationModal>
    </>
  );
}
